import React, { useMemo, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { SearchIcon, ChevronDownIcon, EyeIcon, CopyIcon, EditIcon, Trash2Icon, CheckCircleIcon, XCircleIcon, XIcon, ShieldCheckIcon, PlusIcon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { ModalDialog } from '../components/ui/ModalDialog';
import { useIsMobile } from '../hooks/useMediaQuery';
import { useRole } from '../hooks/useRole';
// --- Types ---
export interface ReportTemplateSection {
  title: string;
  placeholder: string;
  minRows: number;
}
export interface ReportTemplate {
  id: string;
  name: string;
  tipoEsame: string;
  category: 'RM' | 'TAC' | 'ECO' | 'RX' | 'MAMMO';
  standard: boolean;
  medicoAssociato: string[];
  creatoDa: string;
  dataCreazione: string;
  ultimaModifica: string;
  descrizione: string;
  struttura: ReportTemplateSection[];
}
// --- Mock Data ---
export const mockReportTemplates: ReportTemplate[] = [{
  id: 'TPL-001',
  name: 'Referto RM Encefalo Standard',
  tipoEsame: 'Risonanza Magnetica',
  category: 'RM',
  standard: true,
  medicoAssociato: ['Tutti i medici'],
  creatoDa: 'Superadmin',
  dataCreazione: '2025-01-15',
  ultimaModifica: '2026-02-20',
  descrizione: "Modello standard per risonanza magnetica dell'encefalo. Include sezioni per indicazione clinica, tecnica, reperti e conclusioni.",
  struttura: [{
    title: 'Indicazione clinica',
    placeholder: 'Inserire indicazione clinica...',
    minRows: 2
  }, {
    title: 'Tecnica di esame',
    placeholder: 'Descrivere la tecnica utilizzata...',
    minRows: 2
  }, {
    title: 'Reperti',
    placeholder: 'Descrivere i reperti...',
    minRows: 5
  }, {
    title: 'Conclusioni',
    placeholder: 'Inserire le conclusioni diagnostiche...',
    minRows: 3
  }]
}, {
  id: 'TPL-002',
  name: 'Referto RM con Mezzo di Contrasto',
  tipoEsame: 'Risonanza Magnetica',
  category: 'RM',
  standard: true,
  medicoAssociato: ['Tutti i medici'],
  creatoDa: 'Superadmin',
  dataCreazione: '2025-01-15',
  ultimaModifica: '2026-01-10',
  descrizione: 'Modello per risonanza magnetica con somministrazione di mezzo di contrasto paramagnetico.',
  struttura: [{
    title: 'Indicazione clinica',
    placeholder: 'Inserire indicazione clinica...',
    minRows: 2
  }, {
    title: 'Tecnica di esame e contrasto',
    placeholder: 'Descrivere tecnica e mezzo di contrasto utilizzato...',
    minRows: 3
  }, {
    title: 'Reperti pre-contrasto',
    placeholder: 'Descrivere i reperti pre-contrasto...',
    minRows: 4
  }, {
    title: 'Reperti post-contrasto',
    placeholder: 'Descrivere i reperti post-contrasto...',
    minRows: 4
  }, {
    title: 'Conclusioni',
    placeholder: 'Inserire le conclusioni diagnostiche...',
    minRows: 3
  }]
}, {
  id: 'TPL-003',
  name: 'Referto TAC Torace Standard',
  tipoEsame: 'TAC',
  category: 'TAC',
  standard: true,
  medicoAssociato: ['Tutti i medici'],
  creatoDa: 'Superadmin',
  dataCreazione: '2025-02-01',
  ultimaModifica: '2026-03-05',
  descrizione: 'Modello standard per tomografia computerizzata del torace senza e con mezzo di contrasto.',
  struttura: [{
    title: 'Indicazione clinica',
    placeholder: 'Inserire indicazione clinica...',
    minRows: 2
  }, {
    title: 'Tecnica',
    placeholder: 'Descrivere la tecnica...',
    minRows: 2
  }, {
    title: 'Reperti polmonari',
    placeholder: 'Descrivere i reperti polmonari...',
    minRows: 4
  }, {
    title: 'Mediastino e strutture vascolari',
    placeholder: 'Descrivere mediastino...',
    minRows: 3
  }, {
    title: 'Conclusioni',
    placeholder: 'Inserire le conclusioni...',
    minRows: 3
  }]
}, {
  id: 'TPL-004',
  name: 'Referto TAC Addome Completo',
  tipoEsame: 'TAC',
  category: 'TAC',
  standard: true,
  medicoAssociato: ['Tutti i medici'],
  creatoDa: 'Superadmin',
  dataCreazione: '2025-02-01',
  ultimaModifica: '2026-02-28',
  descrizione: 'Modello per TAC addome completo con studio degli organi addominali e pelvici.',
  struttura: [{
    title: 'Indicazione clinica',
    placeholder: 'Inserire indicazione clinica...',
    minRows: 2
  }, {
    title: 'Tecnica',
    placeholder: 'Descrivere la tecnica...',
    minRows: 2
  }, {
    title: 'Fegato e vie biliari',
    placeholder: 'Descrivere fegato e vie biliari...',
    minRows: 3
  }, {
    title: 'Pancreas, milza, reni',
    placeholder: 'Descrivere pancreas, milza, reni...',
    minRows: 3
  }, {
    title: 'Intestino e pelvi',
    placeholder: 'Descrivere intestino e pelvi...',
    minRows: 3
  }, {
    title: 'Conclusioni',
    placeholder: 'Inserire le conclusioni...',
    minRows: 3
  }]
}, {
  id: 'TPL-005',
  name: 'Referto Ecografia Addome Completa',
  tipoEsame: 'Ecografia',
  category: 'ECO',
  standard: true,
  medicoAssociato: ['Tutti i medici'],
  creatoDa: 'Superadmin',
  dataCreazione: '2025-03-10',
  ultimaModifica: '2026-03-01',
  descrizione: 'Modello standard per ecografia addominale completa con studio di tutti gli organi parenchimatosi.',
  struttura: [{
    title: 'Indicazione clinica',
    placeholder: 'Inserire indicazione clinica...',
    minRows: 2
  }, {
    title: 'Fegato',
    placeholder: 'Descrivere il fegato...',
    minRows: 3
  }, {
    title: 'Colecisti e vie biliari',
    placeholder: 'Descrivere colecisti e vie biliari...',
    minRows: 2
  }, {
    title: 'Pancreas',
    placeholder: 'Descrivere il pancreas...',
    minRows: 2
  }, {
    title: 'Milza e reni',
    placeholder: 'Descrivere milza e reni...',
    minRows: 3
  }, {
    title: 'Conclusioni',
    placeholder: 'Inserire le conclusioni...',
    minRows: 3
  }]
}, {
  id: 'TPL-006',
  name: 'Referto Ecografia Tiroidea',
  tipoEsame: 'Ecografia',
  category: 'ECO',
  standard: true,
  medicoAssociato: ['Tutti i medici'],
  creatoDa: 'Superadmin',
  dataCreazione: '2025-03-10',
  ultimaModifica: '2026-01-20',
  descrizione: 'Modello per ecografia della tiroide con classificazione TI-RADS.',
  struttura: [{
    title: 'Indicazione clinica',
    placeholder: 'Inserire indicazione clinica...',
    minRows: 2
  }, {
    title: 'Lobo destro',
    placeholder: 'Descrivere il lobo destro...',
    minRows: 3
  }, {
    title: 'Lobo sinistro',
    placeholder: 'Descrivere il lobo sinistro...',
    minRows: 3
  }, {
    title: 'Istmo',
    placeholder: "Descrivere l'istmo...",
    minRows: 2
  }, {
    title: 'Linfonodi',
    placeholder: 'Descrivere i linfonodi cervicali...',
    minRows: 2
  }, {
    title: 'Conclusioni e classificazione TI-RADS',
    placeholder: 'Inserire le conclusioni...',
    minRows: 3
  }]
}, {
  id: 'TPL-007',
  name: 'Referto Radiografia Torace Standard',
  tipoEsame: 'Radiografia',
  category: 'RX',
  standard: true,
  medicoAssociato: ['Tutti i medici'],
  creatoDa: 'Superadmin',
  dataCreazione: '2025-04-01',
  ultimaModifica: '2026-02-15',
  descrizione: 'Modello standard per radiografia del torace in due proiezioni.',
  struttura: [{
    title: 'Indicazione clinica',
    placeholder: 'Inserire indicazione clinica...',
    minRows: 2
  }, {
    title: 'Tecnica',
    placeholder: 'Proiezioni eseguite...',
    minRows: 1
  }, {
    title: 'Reperti',
    placeholder: 'Descrivere i reperti...',
    minRows: 4
  }, {
    title: 'Conclusioni',
    placeholder: 'Inserire le conclusioni...',
    minRows: 2
  }]
}, {
  id: 'TPL-008',
  name: 'Referto Radiografia Colonna Vertebrale',
  tipoEsame: 'Radiografia',
  category: 'RX',
  standard: true,
  medicoAssociato: ['Tutti i medici'],
  creatoDa: 'Superadmin',
  dataCreazione: '2025-04-01',
  ultimaModifica: '2026-01-30',
  descrizione: 'Modello per radiografia della colonna vertebrale con studio dei segmenti.',
  struttura: [{
    title: 'Indicazione clinica',
    placeholder: 'Inserire indicazione clinica...',
    minRows: 2
  }, {
    title: 'Segmento esaminato',
    placeholder: 'Specificare il segmento...',
    minRows: 1
  }, {
    title: 'Reperti',
    placeholder: 'Descrivere i reperti...',
    minRows: 5
  }, {
    title: 'Conclusioni',
    placeholder: 'Inserire le conclusioni...',
    minRows: 3
  }]
}, {
  id: 'TPL-009',
  name: 'Referto Mammografia Bilaterale',
  tipoEsame: 'Mammografia',
  category: 'MAMMO',
  standard: true,
  medicoAssociato: ['Tutti i medici'],
  creatoDa: 'Superadmin',
  dataCreazione: '2025-05-01',
  ultimaModifica: '2026-03-08',
  descrizione: 'Modello per mammografia bilaterale con classificazione BI-RADS.',
  struttura: [{
    title: 'Indicazione clinica',
    placeholder: 'Inserire indicazione clinica...',
    minRows: 2
  }, {
    title: 'Tecnica',
    placeholder: 'Proiezioni eseguite...',
    minRows: 1
  }, {
    title: 'Mammella destra',
    placeholder: 'Descrivere la mammella destra...',
    minRows: 3
  }, {
    title: 'Mammella sinistra',
    placeholder: 'Descrivere la mammella sinistra...',
    minRows: 3
  }, {
    title: 'Conclusioni e classificazione BI-RADS',
    placeholder: 'Inserire le conclusioni...',
    minRows: 3
  }]
}, {
  id: 'TPL-010',
  name: 'Referto RM Ginocchio — Dr. Verdi',
  tipoEsame: 'Risonanza Magnetica',
  category: 'RM',
  standard: false,
  medicoAssociato: ['Dr. S. Verdi'],
  creatoDa: 'Dr. S. Verdi',
  dataCreazione: '2025-09-12',
  ultimaModifica: '2026-03-10',
  descrizione: 'Modello personalizzato del Dr. Verdi per RM del ginocchio con focus su menischi e legamenti.',
  struttura: [{
    title: 'Indicazione clinica',
    placeholder: 'Inserire indicazione clinica...',
    minRows: 2
  }, {
    title: 'Tecnica',
    placeholder: 'Descrivere la tecnica...',
    minRows: 2
  }, {
    title: 'Menischi',
    placeholder: 'Descrivere i menischi...',
    minRows: 3
  }, {
    title: 'Legamenti',
    placeholder: 'Descrivere i legamenti...',
    minRows: 3
  }, {
    title: 'Cartilagine e osso',
    placeholder: 'Descrivere cartilagine e osso...',
    minRows: 3
  }, {
    title: 'Conclusioni',
    placeholder: 'Inserire le conclusioni...',
    minRows: 3
  }]
}, {
  id: 'TPL-011',
  name: 'Referto TAC Cranio — Dr. Neri',
  tipoEsame: 'TAC',
  category: 'TAC',
  standard: false,
  medicoAssociato: ['Dr. A. Neri'],
  creatoDa: 'Dr. A. Neri',
  dataCreazione: '2025-10-05',
  ultimaModifica: '2026-02-18',
  descrizione: 'Modello personalizzato del Dr. Neri per TAC cranio con sezioni dettagliate.',
  struttura: [{
    title: 'Indicazione clinica',
    placeholder: 'Inserire indicazione clinica...',
    minRows: 2
  }, {
    title: 'Tecnica',
    placeholder: 'Descrivere la tecnica...',
    minRows: 2
  }, {
    title: 'Parenchima cerebrale',
    placeholder: 'Descrivere il parenchima...',
    minRows: 4
  }, {
    title: 'Sistema ventricolare',
    placeholder: 'Descrivere il sistema ventricolare...',
    minRows: 2
  }, {
    title: 'Strutture ossee',
    placeholder: 'Descrivere le strutture ossee...',
    minRows: 2
  }, {
    title: 'Conclusioni',
    placeholder: 'Inserire le conclusioni...',
    minRows: 3
  }]
}, {
  id: 'TPL-012',
  name: 'Referto Ecografia Muscolo-Tendinea — Dr. Gialli',
  tipoEsame: 'Ecografia',
  category: 'ECO',
  standard: false,
  medicoAssociato: ['Dr. M. Gialli'],
  creatoDa: 'Dr. M. Gialli',
  dataCreazione: '2025-11-20',
  ultimaModifica: '2026-03-02',
  descrizione: 'Modello personalizzato del Dr. Gialli per ecografia muscolo-tendinea.',
  struttura: [{
    title: 'Indicazione clinica',
    placeholder: 'Inserire indicazione clinica...',
    minRows: 2
  }, {
    title: 'Distretto esaminato',
    placeholder: 'Specificare il distretto...',
    minRows: 1
  }, {
    title: 'Tendini',
    placeholder: 'Descrivere i tendini...',
    minRows: 3
  }, {
    title: 'Muscoli',
    placeholder: 'Descrivere i muscoli...',
    minRows: 3
  }, {
    title: 'Conclusioni',
    placeholder: 'Inserire le conclusioni...',
    minRows: 3
  }]
}, {
  id: 'TPL-013',
  name: 'Referto RM Spalla — Dr. Verdi',
  tipoEsame: 'Risonanza Magnetica',
  category: 'RM',
  standard: false,
  medicoAssociato: ['Dr. S. Verdi'],
  creatoDa: 'Dr. S. Verdi',
  dataCreazione: '2025-12-01',
  ultimaModifica: '2026-03-09',
  descrizione: 'Modello personalizzato del Dr. Verdi per RM della spalla con focus su cuffia dei rotatori.',
  struttura: [{
    title: 'Indicazione clinica',
    placeholder: 'Inserire indicazione clinica...',
    minRows: 2
  }, {
    title: 'Tecnica',
    placeholder: 'Descrivere la tecnica...',
    minRows: 2
  }, {
    title: 'Cuffia dei rotatori',
    placeholder: 'Descrivere la cuffia dei rotatori...',
    minRows: 4
  }, {
    title: 'Articolazione gleno-omerale',
    placeholder: "Descrivere l'articolazione...",
    minRows: 3
  }, {
    title: 'Conclusioni',
    placeholder: 'Inserire le conclusioni...',
    minRows: 3
  }]
}, {
  id: 'TPL-014',
  name: 'Referto RX Anca — Dr. Neri',
  tipoEsame: 'Radiografia',
  category: 'RX',
  standard: false,
  medicoAssociato: ['Dr. A. Neri'],
  creatoDa: 'Dr. A. Neri',
  dataCreazione: '2026-01-10',
  ultimaModifica: '2026-03-06',
  descrizione: "Modello personalizzato del Dr. Neri per radiografia dell'anca.",
  struttura: [{
    title: 'Indicazione clinica',
    placeholder: 'Inserire indicazione clinica...',
    minRows: 2
  }, {
    title: 'Proiezioni',
    placeholder: 'Specificare le proiezioni...',
    minRows: 1
  }, {
    title: 'Reperti',
    placeholder: 'Descrivere i reperti...',
    minRows: 4
  }, {
    title: 'Conclusioni',
    placeholder: 'Inserire le conclusioni...',
    minRows: 3
  }]
}, {
  id: 'TPL-015',
  name: 'Referto Ecografia Mammaria — Dr. Gialli',
  tipoEsame: 'Ecografia',
  category: 'ECO',
  standard: false,
  medicoAssociato: ['Dr. M. Gialli'],
  creatoDa: 'Dr. M. Gialli',
  dataCreazione: '2026-02-05',
  ultimaModifica: '2026-03-11',
  descrizione: 'Modello personalizzato del Dr. Gialli per ecografia mammaria bilaterale.',
  struttura: [{
    title: 'Indicazione clinica',
    placeholder: 'Inserire indicazione clinica...',
    minRows: 2
  }, {
    title: 'Mammella destra',
    placeholder: 'Descrivere la mammella destra...',
    minRows: 3
  }, {
    title: 'Mammella sinistra',
    placeholder: 'Descrivere la mammella sinistra...',
    minRows: 3
  }, {
    title: 'Cavi ascellari',
    placeholder: 'Descrivere i cavi ascellari...',
    minRows: 2
  }, {
    title: 'Conclusioni',
    placeholder: 'Inserire le conclusioni...',
    minRows: 3
  }]
}, {
  id: 'TPL-016',
  name: 'Referto RM Rachide Lombare Standard',
  tipoEsame: 'Risonanza Magnetica',
  category: 'RM',
  standard: true,
  medicoAssociato: ['Tutti i medici'],
  creatoDa: 'Superadmin',
  dataCreazione: '2025-06-15',
  ultimaModifica: '2026-02-25',
  descrizione: 'Modello standard per RM del rachide lombare con studio dei dischi intervertebrali.',
  struttura: [{
    title: 'Indicazione clinica',
    placeholder: 'Inserire indicazione clinica...',
    minRows: 2
  }, {
    title: 'Tecnica',
    placeholder: 'Descrivere la tecnica...',
    minRows: 2
  }, {
    title: 'Corpi vertebrali',
    placeholder: 'Descrivere i corpi vertebrali...',
    minRows: 3
  }, {
    title: 'Dischi intervertebrali',
    placeholder: 'Descrivere i dischi...',
    minRows: 4
  }, {
    title: 'Canale spinale',
    placeholder: 'Descrivere il canale spinale...',
    minRows: 3
  }, {
    title: 'Conclusioni',
    placeholder: 'Inserire le conclusioni...',
    minRows: 3
  }]
}];
// Simulated role
const CURRENT_ROLE: 'superadmin' | 'medico' = 'superadmin';
const CURRENT_USER = 'Superadmin';
function TruncatedCell({
  text
}: {
  text: string;
}) {
  const [show, setShow] = useState(false);
  const textRef = useRef<HTMLSpanElement>(null);
  const isMobileView = useIsMobile();
  const handleMouseEnter = () => {
    if (isMobileView) return;
    if (textRef.current) {
      if (textRef.current.scrollWidth > textRef.current.clientWidth) {
        setShow(true);
      }
    }
  };
  return <div className="relative min-w-0" onMouseEnter={handleMouseEnter} onMouseLeave={() => setShow(false)} data-id="element-2382">
      <span ref={textRef} className="block truncate" data-id="element-2383">
        {text}
      </span>
      {show && !isMobileView && <div className="fixed px-2.5 py-1.5 bg-text-primary text-white text-xs rounded-md whitespace-nowrap pointer-events-none shadow-lg" style={{
      zIndex: 9999,
      transform: 'translateY(-100%) translateY(-6px)'
    }} ref={el => {
      if (el) {
        const parent = el.parentElement?.getBoundingClientRect();
        if (parent) {
          el.style.top = `${parent.top}px`;
          el.style.left = `${parent.left}px`;
        }
      }
    }} data-id="element-2384">
          {text}
        </div>}
    </div>;
}
// Exam categories the logged-in Medico (Dr. S. Verdi) is enabled to perform
const MEDICO_ENABLED_CATEGORIES: ReportTemplate['category'][] = ['RM', 'TAC', 'ECO', 'RX'];
export function ReportTemplates() {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const {
    role
  } = useRole();
  const CURRENT_USER = role === 'medico' ? 'Dr. S. Verdi' : 'Superadmin';
  const isMedico = role === 'medico';
  // State
  const [searchQuery, setSearchQuery] = useState('');
  const [tipoEsameFilter, setTipoEsameFilter] = useState('Tutti');
  const [standardFilter, setStandardFilter] = useState('Tutti');
  const [medicoFilter, setMedicoFilter] = useState('Tutti');
  const [currentPage, setCurrentPage] = useState(1);
  const [templates, setTemplates] = useState<ReportTemplate[]>(mockReportTemplates);
  const [deleteTarget, setDeleteTarget] = useState<ReportTemplate | null>(null);
  const [toastMessage, setToastMessage] = useState<{
    type: 'success' | 'error';
    text: string;
  } | null>(null);
  const itemsPerPage = 10;
  const showToast = (type: 'success' | 'error', text: string) => {
    setToastMessage({
      type,
      text
    });
    setTimeout(() => setToastMessage(null), 4000);
  };
  // Derived
  const uniqueTipiEsame = useMemo(() => Array.from(new Set(templates.map(t => t.tipoEsame))).sort(), [templates]);
  const uniqueMedici = useMemo(() => {
    const all = new Set<string>();
    templates.forEach(t => t.medicoAssociato.forEach(m => {
      if (m !== 'Tutti i medici') all.add(m);
    }));
    return Array.from(all).sort();
  }, [templates]);
  // Helper: replace doctor name with "Tu" when it belongs to the logged-in medico
  const displayDoctor = (name: string) => {
    if (isMedico && name === CURRENT_USER) return 'Tu';
    return name;
  };
  const displayDoctorList = (names: string[]) => {
    return names.map(n => displayDoctor(n)).join(', ');
  };
  const filteredTemplates = useMemo(() => {
    let result = [...templates];
    if (isMedico) {
      // Only show templates for exam types the medico is enabled to perform
      result = result.filter(t => MEDICO_ENABLED_CATEGORIES.includes(t.category));
      // Then filter to standard templates + own custom templates
      result = result.filter(t => t.standard || t.creatoDa === CURRENT_USER);
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(t => t.name.toLowerCase().includes(q) || t.tipoEsame.toLowerCase().includes(q) || t.medicoAssociato.some(m => m.toLowerCase().includes(q)) || t.creatoDa.toLowerCase().includes(q));
    }
    if (tipoEsameFilter !== 'Tutti') {
      result = result.filter(t => t.tipoEsame === tipoEsameFilter);
    }
    if (standardFilter !== 'Tutti') {
      result = result.filter(t => standardFilter === 'Standard' ? t.standard : !t.standard);
    }
    if (medicoFilter !== 'Tutti') {
      result = result.filter(t => t.medicoAssociato.includes(medicoFilter));
    }
    return result;
  }, [templates, searchQuery, tipoEsameFilter, standardFilter, medicoFilter]);
  const totalPages = Math.ceil(filteredTemplates.length / itemsPerPage);
  const paginatedTemplates = filteredTemplates.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
  // Handlers
  const handleDuplicate = (template: ReportTemplate) => {
    const newId = `TPL-${String(templates.length + 1).padStart(3, '0')}`;
    const newTemplate: ReportTemplate = {
      ...template,
      id: newId,
      name: `${template.name} (Copia)`,
      standard: false,
      medicoAssociato: [CURRENT_USER],
      creatoDa: CURRENT_USER,
      dataCreazione: new Date().toISOString().split('T')[0],
      ultimaModifica: new Date().toISOString().split('T')[0]
    };
    setTemplates(prev => [...prev, newTemplate]);
    navigate(`/templates/${newId}?edit=true`, {
      state: {
        template: newTemplate
      }
    });
  };
  const handleDelete = () => {
    if (!deleteTarget) return;
    setTemplates(prev => prev.filter(t => t.id !== deleteTarget.id));
    showToast('success', `Modello "${deleteTarget.name}" eliminato correttamente.`);
    setDeleteTarget(null);
  };
  const canEdit = (template: ReportTemplate) => {
    if (role === 'superadmin') return true;
    return !template.standard && template.creatoDa === CURRENT_USER;
  };
  const canDelete = (template: ReportTemplate) => {
    if (role === 'superadmin') return true;
    return !template.standard && template.creatoDa === CURRENT_USER;
  };
  const renderActions = (template: ReportTemplate) => <div className="flex items-center justify-end gap-1" onClick={e => e.stopPropagation()} data-id="element-2385">
      {isMobile ? <>
          <button onClick={() => navigate(`/templates/${template.id}`)} className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-text-secondary hover:text-medical-green hover:bg-medical-green-50 rounded transition-colors min-h-[36px]" aria-label="Visualizza dettaglio" data-id="element-2386">
            <EyeIcon size={15} data-id="element-2387" /> Dettaglio
          </button>
          <button onClick={() => handleDuplicate(template)} className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-text-secondary hover:text-status-info hover:bg-status-info-bg rounded transition-colors min-h-[36px]" aria-label="Duplica modello" data-id="element-2388">
            <CopyIcon size={15} data-id="element-2389" /> Duplica
          </button>
          {canEdit(template) && <button onClick={() => navigate(`/templates/${template.id}`)} className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-text-secondary hover:text-medical-orange hover:bg-medical-orange-50 rounded transition-colors min-h-[36px]" aria-label="Modifica modello" data-id="element-2390">
              <EditIcon size={15} data-id="element-2391" /> Modifica
            </button>}
          {canDelete(template) && <button onClick={() => setDeleteTarget(template)} className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-text-secondary hover:text-status-error hover:bg-status-error-bg rounded transition-colors min-h-[36px]" aria-label="Elimina modello" data-id="element-2392">
              <Trash2Icon size={15} data-id="element-2393" /> Elimina
            </button>}
        </> : <>
          <div className="relative group/action" data-id="element-2394">
            <button onClick={() => navigate(`/templates/${template.id}`)} className="p-1.5 text-text-secondary hover:text-medical-green hover:bg-medical-green-50 rounded transition-colors" aria-label="Visualizza dettaglio" data-id="element-2395">
              <EyeIcon size={15} data-id="element-2396" />
            </button>
            <div className="fixed px-2 py-1 bg-text-primary text-white text-xs rounded whitespace-nowrap opacity-0 group-hover/action:opacity-100 transition-opacity pointer-events-none shadow-lg" style={{
          zIndex: 9999
        }} ref={el => {
          if (el) {
            const btn = el.previousElementSibling as HTMLElement;
            if (btn) {
              const rect = btn.getBoundingClientRect();
              el.style.top = `${rect.top - el.offsetHeight - 6}px`;
              el.style.left = `${rect.left + rect.width / 2 - el.offsetWidth / 2}px`;
            }
          }
        }} data-id="element-2397">
              Visualizza dettaglio
            </div>
          </div>

          <div className="relative group/action" data-id="element-2398">
            <button onClick={() => handleDuplicate(template)} className="p-1.5 text-text-secondary hover:text-status-info hover:bg-status-info-bg rounded transition-colors" aria-label="Duplica modello" data-id="element-2399">
              <CopyIcon size={15} data-id="element-2400" />
            </button>
            <div className="fixed px-2 py-1 bg-text-primary text-white text-xs rounded whitespace-nowrap opacity-0 group-hover/action:opacity-100 transition-opacity pointer-events-none shadow-lg" style={{
          zIndex: 9999
        }} ref={el => {
          if (el) {
            const btn = el.previousElementSibling as HTMLElement;
            if (btn) {
              const rect = btn.getBoundingClientRect();
              el.style.top = `${rect.top - el.offsetHeight - 6}px`;
              el.style.left = `${rect.left + rect.width / 2 - el.offsetWidth / 2}px`;
            }
          }
        }} data-id="element-2401">
              Duplica modello
            </div>
          </div>

          {canEdit(template) && <div className="relative group/action" data-id="element-2402">
              <button onClick={() => navigate(`/templates/${template.id}`)} className="p-1.5 text-text-secondary hover:text-medical-orange hover:bg-medical-orange-50 rounded transition-colors" aria-label="Modifica modello" data-id="element-2403">
                <EditIcon size={15} data-id="element-2404" />
              </button>
              <div className="fixed px-2 py-1 bg-text-primary text-white text-xs rounded whitespace-nowrap opacity-0 group-hover/action:opacity-100 transition-opacity pointer-events-none shadow-lg" style={{
          zIndex: 9999
        }} ref={el => {
          if (el) {
            const btn = el.previousElementSibling as HTMLElement;
            if (btn) {
              const rect = btn.getBoundingClientRect();
              el.style.top = `${rect.top - el.offsetHeight - 6}px`;
              el.style.left = `${rect.left + rect.width / 2 - el.offsetWidth / 2}px`;
            }
          }
        }} data-id="element-2405">
                Modifica modello
              </div>
            </div>}

          {canDelete(template) && <div className="relative group/action" data-id="element-2406">
              <button onClick={() => setDeleteTarget(template)} className="p-1.5 text-text-secondary hover:text-status-error hover:bg-status-error-bg rounded transition-colors" aria-label="Elimina modello" data-id="element-2407">
                <Trash2Icon size={15} data-id="element-2408" />
              </button>
              <div className="fixed px-2 py-1 bg-text-primary text-white text-xs rounded whitespace-nowrap opacity-0 group-hover/action:opacity-100 transition-opacity pointer-events-none shadow-lg" style={{
          zIndex: 9999
        }} ref={el => {
          if (el) {
            const btn = el.previousElementSibling as HTMLElement;
            if (btn) {
              const rect = btn.getBoundingClientRect();
              el.style.top = `${rect.top - el.offsetHeight - 6}px`;
              el.style.left = `${rect.left + rect.width / 2 - el.offsetWidth / 2}px`;
            }
          }
        }} data-id="element-2409">
                Elimina modello
              </div>
            </div>}
        </>}
    </div>;
  return <div className="space-y-6 w-full min-w-0 pb-12" data-id="element-2410">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4" data-id="element-2411">
        <div data-id="element-2412">
          <h1 className="text-2xl md:text-page-title mb-1 md:mb-2 text-text-primary font-bold" data-id="element-2413">
            Modelli Referto
          </h1>
          <p className="text-sm md:text-body text-text-secondary" data-id="element-2414">
            Libreria globale dei modelli di refertazione. Gestione standard e
            personalizzati.
          </p>
        </div>
        <button onClick={() => navigate('/templates/new')} className="medical-btn-primary w-full md:w-auto" data-id="element-2415">
          <PlusIcon size={18} className="mr-1.5" data-id="element-2416" /> Crea modello
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-3 md:items-center bg-white p-4 rounded-medical-lg shadow-sm border border-clinical-gray-100" data-id="element-2417">
        <div className="relative w-full md:w-64" data-id="element-2418">
          <SearchIcon size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-tertiary pointer-events-none" data-id="element-2419" />
          <input type="text" value={searchQuery} onChange={e => {
          setSearchQuery(e.target.value);
          setCurrentPage(1);
        }} placeholder="Cerca modello, esame, medico..." className="medical-input pl-9 text-sm h-9 w-full" data-id="element-2420" />
        </div>

        <div className="relative w-full md:w-auto" data-id="element-2421">
          <select value={tipoEsameFilter} onChange={e => {
          setTipoEsameFilter(e.target.value);
          setCurrentPage(1);
        }} className="medical-input text-sm h-9 pr-8 appearance-none cursor-pointer w-full md:w-auto" data-id="element-2422">
            <option value="Tutti" data-id="element-2423">Tipo esame: Tutti</option>
            {uniqueTipiEsame.map(t => <option key={t} value={t} data-id="element-2424">
                {t}
              </option>)}
          </select>
          <ChevronDownIcon size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-text-tertiary pointer-events-none" data-id="element-2425" />
        </div>

        <div className="relative w-full md:w-auto" data-id="element-2426">
          <select value={standardFilter} onChange={e => {
          setStandardFilter(e.target.value);
          setCurrentPage(1);
        }} className="medical-input text-sm h-9 pr-8 appearance-none cursor-pointer w-full md:w-auto" data-id="element-2427">
            <option value="Tutti" data-id="element-2428">Tipo: Tutti</option>
            <option value="Standard" data-id="element-2429">Standard</option>
            <option value="Personalizzato" data-id="element-2430">Personalizzato</option>
          </select>
          <ChevronDownIcon size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-text-tertiary pointer-events-none" data-id="element-2431" />
        </div>

        <div className="relative w-full md:w-auto" data-id="element-2432">
          <select value={medicoFilter} onChange={e => {
          setMedicoFilter(e.target.value);
          setCurrentPage(1);
        }} className="medical-input text-sm h-9 pr-8 appearance-none cursor-pointer w-full md:w-auto" data-id="element-2433">
            <option value="Tutti" data-id="element-2434">Medico: Tutti</option>
            {uniqueMedici.map(m => <option key={m} value={m} data-id="element-2435">
                {m}
              </option>)}
          </select>
          <ChevronDownIcon size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-text-tertiary pointer-events-none" data-id="element-2436" />
        </div>
      </div>

      {/* Table / Cards */}
      {!isMobile ? <div className="w-full max-w-full bg-white rounded-medical-lg shadow-card border border-clinical-gray-100" style={{
      overflowX: 'auto',
      overflowY: 'visible'
    }} data-id="element-2437">
          <table className="w-full text-left border-collapse" style={{
        tableLayout: 'fixed',
        minWidth: '920px'
      }} data-id="element-2438">
            <colgroup data-id="element-2439">
              <col style={{
            width: '24%'
          }} data-id="element-2440" />
              <col style={{
            width: '15%'
          }} data-id="element-2441" />
              <col style={{
            width: '10%'
          }} data-id="element-2442" />
              <col style={{
            width: '18%'
          }} data-id="element-2443" />
              <col style={{
            width: '13%'
          }} data-id="element-2444" />
              <col style={{
            width: '20%'
          }} data-id="element-2445" />
            </colgroup>
            <thead data-id="element-2446">
              <tr className="bg-clinical-gray border-b border-clinical-gray-200" data-id="element-2447">
                <th className="px-4 py-3 text-table-header text-text-secondary whitespace-nowrap" data-id="element-2448">
                  Nome modello
                </th>
                <th className="px-4 py-3 text-table-header text-text-secondary whitespace-nowrap" data-id="element-2449">
                  Tipo di esame
                </th>
                <th className="px-4 py-3 text-table-header text-text-secondary whitespace-nowrap" data-id="element-2450">
                  Standard
                </th>
                <th className="px-4 py-3 text-table-header text-text-secondary whitespace-nowrap" data-id="element-2451">
                  Medico / Medici
                </th>
                <th className="px-4 py-3 text-table-header text-text-secondary whitespace-nowrap" data-id="element-2452">
                  Creato da
                </th>
                <th className="px-4 py-3 text-table-header text-text-secondary whitespace-nowrap text-right" data-id="element-2453">
                  Azioni
                </th>
              </tr>
            </thead>
            <tbody data-id="element-2454">
              {paginatedTemplates.length === 0 ? <tr data-id="element-2455">
                  <td colSpan={6} className="px-4 py-8 text-center text-text-secondary text-body" data-id="element-2456">
                    Nessun modello trovato con i filtri correnti.
                  </td>
                </tr> : paginatedTemplates.map(template => <tr key={template.id} className="border-b border-clinical-gray-100 last:border-0 hover:bg-clinical-gray/30 transition-colors" data-id="element-2457">
                    <td className="px-4 py-3 text-body font-medium text-text-primary overflow-hidden" data-id="element-2458">
                      <TruncatedCell text={template.name} data-id="element-2459" />
                    </td>
                    <td className="px-4 py-3 text-body text-text-secondary overflow-hidden" data-id="element-2460">
                      <TruncatedCell text={template.tipoEsame} data-id="element-2461" />
                    </td>
                    <td className="px-4 py-3" data-id="element-2462">
                      {template.standard ? <span className="inline-flex items-center gap-1 text-xs font-medium text-medical-green bg-medical-green-50 px-2 py-0.5 rounded-full" data-id="element-2463">
                          <ShieldCheckIcon size={12} data-id="element-2464" /> Sì
                        </span> : <span className="text-xs text-text-tertiary" data-id="element-2465">No</span>}
                    </td>
                    <td className="px-4 py-3 text-body text-text-secondary overflow-hidden" data-id="element-2466">
                      <TruncatedCell text={displayDoctorList(template.medicoAssociato)} data-id="element-2467" />
                    </td>
                    <td className="px-4 py-3 text-body text-text-secondary overflow-hidden" data-id="element-2468">
                      <TruncatedCell text={displayDoctor(template.creatoDa)} data-id="element-2469" />
                    </td>
                    <td className="px-4 py-3" data-id="element-2470">{renderActions(template)}</td>
                  </tr>)}
            </tbody>
          </table>
        </div> : <div className="space-y-3" data-id="element-2471">
          {paginatedTemplates.length === 0 ? <div className="p-6 text-center text-text-secondary text-body bg-white rounded-medical-lg shadow-card border border-clinical-gray-100" data-id="element-2472">
              Nessun modello trovato con i filtri correnti.
            </div> : paginatedTemplates.map(template => <div key={template.id} className="bg-white rounded-medical-lg shadow-card border border-clinical-gray-100 p-4 space-y-3" data-id="element-2473">
                <div className="flex justify-between items-start" data-id="element-2474">
                  <div className="min-w-0 mr-3" data-id="element-2475">
                    <span className="text-sm font-medium text-text-primary block mb-0.5" data-id="element-2476">
                      {template.name}
                    </span>
                    <span className="text-xs text-text-tertiary block" data-id="element-2477">
                      {template.tipoEsame}
                    </span>
                  </div>
                  {template.standard ? <span className="inline-flex items-center gap-1 text-xs font-medium text-medical-green bg-medical-green-50 px-2 py-0.5 rounded-full shrink-0" data-id="element-2478">
                      <ShieldCheckIcon size={12} data-id="element-2479" /> Standard
                    </span> : <span className="text-xs text-text-tertiary bg-clinical-gray-100 px-2 py-0.5 rounded-full shrink-0" data-id="element-2480">
                      Personalizzato
                    </span>}
                </div>
                <div className="grid grid-cols-2 gap-2" data-id="element-2481">
                  <div className="min-w-0" data-id="element-2482">
                    <span className="text-xs text-text-tertiary uppercase tracking-wider block mb-0.5" data-id="element-2483">
                      Medico
                    </span>
                    <span className="text-sm text-text-secondary truncate block" data-id="element-2484">
                      {displayDoctorList(template.medicoAssociato)}
                    </span>
                  </div>
                  <div className="min-w-0" data-id="element-2485">
                    <span className="text-xs text-text-tertiary uppercase tracking-wider block mb-0.5" data-id="element-2486">
                      Creato da
                    </span>
                    <span className="text-sm text-text-secondary truncate block" data-id="element-2487">
                      {displayDoctor(template.creatoDa)}
                    </span>
                  </div>
                </div>
                <div className="pt-3 border-t border-clinical-gray-100 flex justify-end" data-id="element-2488">
                  {renderActions(template)}
                </div>
              </div>)}
        </div>}

      {/* Pagination */}
      {totalPages > 1 && <div className="flex items-center justify-between px-1" data-id="element-2489">
          <span className="text-sm text-text-secondary" data-id="element-2490">
            {filteredTemplates.length} risultati — Pagina {currentPage} di{' '}
            {totalPages}
          </span>
          <div className="flex items-center gap-2" data-id="element-2491">
            <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} className="w-8 h-8 flex items-center justify-center rounded border border-clinical-gray-200 text-text-secondary hover:bg-clinical-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors" data-id="element-2492">
              <ChevronDownIcon size={16} className="rotate-90" data-id="element-2493" />
            </button>
            <div className="flex items-center gap-1" data-id="element-2494">
              {Array.from({
            length: totalPages
          }, (_, i) => i + 1).map(page => <button key={page} onClick={() => setCurrentPage(page)} className={`w-8 h-8 flex items-center justify-center rounded text-sm font-medium transition-colors ${currentPage === page ? 'bg-medical-green text-white border border-medical-green' : 'border border-clinical-gray-200 text-text-secondary hover:bg-clinical-gray-50'}`} data-id="element-2495">
                  {page}
                </button>)}
            </div>
            <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} className="w-8 h-8 flex items-center justify-center rounded border border-clinical-gray-200 text-text-secondary hover:bg-clinical-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors" data-id="element-2496">
              <ChevronDownIcon size={16} className="-rotate-90" data-id="element-2497" />
            </button>
          </div>
        </div>}

      {/* Delete Confirmation Modal */}
      <ModalDialog isOpen={!!deleteTarget} onClose={() => setDeleteTarget(null)} title="Elimina Modello" footer={<>
            <button className="medical-btn-secondary w-full sm:w-auto" onClick={() => setDeleteTarget(null)} data-id="element-2499">
              Annulla
            </button>
            <button className="medical-btn-primary w-full sm:w-auto bg-status-error hover:bg-status-error/90 border-status-error" onClick={handleDelete} data-id="element-2500">
              <Trash2Icon size={16} className="mr-1.5" data-id="element-2501" /> Conferma eliminazione
            </button>
          </>} data-id="element-2498">
        {deleteTarget && <div className="space-y-4" data-id="element-2502">
            <p className="text-sm text-text-secondary" data-id="element-2503">
              Sei sicuro di voler eliminare il modello{' '}
              <span className="font-semibold text-text-primary" data-id="element-2504">
                "{deleteTarget.name}"
              </span>
              ?
            </p>
            {deleteTarget.standard && <div className="bg-status-error-bg border border-status-error/20 rounded-medical-lg p-4" data-id="element-2505">
                <p className="text-sm text-status-error font-medium" data-id="element-2506">
                  Attenzione: questo è un modello standard. L'eliminazione lo
                  rimuoverà per tutti i medici della piattaforma.
                </p>
              </div>}
          </div>}
      </ModalDialog>

      {/* Toast */}
      <AnimatePresence data-id="element-2507">
        {toastMessage && <motion.div initial={{
        opacity: 0,
        y: 50,
        scale: 0.9
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: 20,
        scale: 0.9
      }} className={`fixed bottom-6 left-1/2 -translate-x-1/2 sm:left-auto sm:translate-x-0 sm:right-6 z-50 flex items-center gap-3 px-4 py-3 rounded-medical-lg shadow-modal border bg-white w-[calc(100%-3rem)] sm:w-auto ${toastMessage.type === 'success' ? 'border-status-success/20' : 'border-status-error/20'}`} data-id="element-2508">
            {toastMessage.type === 'success' ? <CheckCircleIcon className="text-status-success shrink-0" size={20} data-id="element-2509" /> : <XCircleIcon className="text-status-error shrink-0" size={20} data-id="element-2510" />}
            <span className="text-sm font-medium text-text-primary" data-id="element-2511">
              {toastMessage.text}
            </span>
            <button onClick={() => setToastMessage(null)} className="ml-2 p-1 text-text-tertiary hover:text-text-primary rounded-full transition-colors" data-id="element-2512">
              <XIcon size={16} data-id="element-2513" />
            </button>
          </motion.div>}
      </AnimatePresence>
    </div>;
}